import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Tag, Spin, message, Empty, Select, Progress, Button, Modal, Input, Popconfirm, Alert } from 'antd';
import ResponsiveTable from '../../components/common/ResponsiveTable';
import ResponsiveTabs from '../../components/common/ResponsiveTabs';
import {
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  ToolOutlined, BookOutlined, PictureOutlined, PlayCircleOutlined,
  DownloadOutlined, VideoCameraOutlined, FileImageOutlined, SendOutlined, FileSearchOutlined,
  EyeOutlined, TrophyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import {
  getUpcomingSessions, getMakeupSlotOptions, createParentRequest, getMyParentRequests, cancelParentRequest,
} from '../../api/parentRequests';
import { downloadCertificate } from '../../api/certificates';
import ChildSelector from '../../components/parent/ChildSelector';
import { apiErrorMessage } from '../../utils/apiError';

const { TextArea } = Input;

const STATUS_LABELS = { present: 'Geldi', absent: 'Gelmedi', planned_absent: 'Mazeretli Devamsız', planned_absent_paid: 'Gelmeyecek' };
const STATUS_COLORS = { present: 'success', absent: 'error', planned_absent: 'warning', planned_absent_paid: 'pink' };
const STATUS_ICONS  = {
  present:         <CheckCircleOutlined />,
  absent:          <CloseCircleOutlined />,
  planned_absent:  <ClockCircleOutlined />,
};

const ParentStudents = () => {
  const [searchParams] = useSearchParams();
  const [dashLoading, setDashLoading]   = useState(true);
  const [children, setChildren]         = useState([]);
  const [selectedId, setSelectedId]     = useState(null);

  const [attendances, setAttendances]   = useState([]);
  const [attLoading, setAttLoading]     = useState(false);
  const [attTotal, setAttTotal]         = useState(0);
  const [attPage, setAttPage]           = useState(1);
  const [attSummary, setAttSummary]         = useState(null);
  const [attSummaryByStudent, setAttSummaryByStudent] = useState({});

  const [makeups, setMakeups]           = useState([]);
  const [mkLoading, setMkLoading]       = useState(false);
  const [mkTotal, setMkTotal]           = useState(0);
  const [mkPage, setMkPage]             = useState(1);

  const [media, setMedia]               = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [preview, setPreview]           = useState(null); // { url, type }

  const [certsByStudent, setCertsByStudent] = useState({});
  const [certLoading, setCertLoading]       = useState(false);

  const [myRequests, setMyRequests]         = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);

  const [requestModalType, setRequestModalType] = useState(null); // 'absence' | 'makeup' | null
  const [upcomingSessions, setUpcomingSessions]  = useState([]);
  const [upcomingLoading, setUpcomingLoading]    = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedMakeupRightId, setSelectedMakeupRightId] = useState(null);
  const [requestNote, setRequestNote]       = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Blob + createObjectURL yerine dogrudan backend'in "?download=1" ile
  // donen Content-Disposition:attachment basligina guveniyoruz — auth web'de
  // cookie ile gittigi icin normal bir <a href> navigasyonu yeterli, ekstra
  // XHR/blob adimina gerek yok (buyuk dosyalarda daha hafif, CORS'a bagimli degil).
  const handleDownloadMedia = (item) => {
    const a = document.createElement('a');
    a.href = item.download_url || `${item.stream_url}?download=1`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Load children + certificates from dashboard
  // URL'den gelen ?student= varsa onu sec (medya bildirimine tiklayinca
  // bildirimde adi gecen ogrenci secili gelsin diye) - gecersiz/baska bir
  // veliye ait id gonderilirse yok sayilip ilk cocuga dusuluyor.
  useEffect(() => {
    api.get('/parent/dashboard')
      .then(res => {
        const kids = res.data.children || [];
        setChildren(kids);
        if (kids.length > 0) {
          const wanted = Number(searchParams.get('student'));
          const match = kids.find(k => k.id === wanted);
          setSelectedId(match ? match.id : kids[0].id);
        }
      })
      .catch(() => message.error('Öğrenci bilgileri yüklenemedi.'))
      .finally(() => setDashLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load attendances
  const loadAttendances = (page = 1, studentId = selectedId) => {
    setAttLoading(true);
    api.get('/parent/attendances', { params: { page, per_page: 10, student_id: studentId || undefined } })
      .then(res => {
        setAttendances(res.data.data || []);
        setAttTotal(res.data.meta?.total || res.data.total || 0);
        setAttSummary(res.data.summary || null);
        setAttSummaryByStudent(res.data.summary_by_student || {});
      })
      .catch(() => message.error('Devam bilgileri yüklenemedi.'))
      .finally(() => setAttLoading(false));
  };

  // Load makeup sessions - secili ogrenciye gore filtrelenir, aksi halde
  // veli birden fazla cocuga sahipse hepsinin kayitlari birlikte gorunup
  // ayni gune denk gelen farkli cocuklarin kayitlari "duplicate" gibi
  // gorunuyordu.
  const loadMakeups = (page = 1, studentId = selectedId) => {
    setMkLoading(true);
    api.get('/parent/makeup-sessions', { params: { page, per_page: 10, student_id: studentId || undefined } })
      .then(res => {
        setMakeups(res.data.data || []);
        setMkTotal(res.data.meta?.total || res.data.total || 0);
      })
      .catch(() => message.error('Telafi dersleri yüklenemedi.'))
      .finally(() => setMkLoading(false));
  };

  // Load media (fotoğraf/video)
  const loadMedia = (studentId = selectedId) => {
    if (!studentId) return;
    setMediaLoading(true);
    api.get(`/students/${studentId}/videos`)
      .then(res => setMedia(res.data || []))
      .catch(() => message.error('Medya yüklenemedi.'))
      .finally(() => setMediaLoading(false));
  };

  // Tamamlanan sertifika programlari icin gercek Certificate kaydini (pdf_path,
  // view_url, certificate_number) yukler - dashboard'daki `certificates` alani
  // sadece ilerleme yuzdesi hesaplayan sentetik bir veri, gercek sertifika
  // GeneratePendingCertificates job'i ile (her 10 dk) ayrica olusuyor/PDF'i
  // uretiliyor - bu yuzden %100 ilerleme ile gercek sertifikanin hazir olmasi
  // arasinda gecikme olabilir.
  const loadCertificates = (studentId = selectedId) => {
    if (!studentId) return;
    setCertLoading(true);
    api.get(`/students/${studentId}/certificates`)
      .then(res => setCertsByStudent(prev => ({ ...prev, [studentId]: res.data || [] })))
      .catch(() => message.error('Sertifikalar yüklenemedi.'))
      .finally(() => setCertLoading(false));
  };

  const handleDownloadCertificate = async (cert) => {
    try {
      const res = await downloadCertificate(cert.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Sertifika indirilemedi.'));
    }
  };

  // Talep gecmisi (veli kendi olusturduklarini gorur, bekleyeni geri cekebilir)
  const loadMyRequests = () => {
    setMyRequestsLoading(true);
    getMyParentRequests()
      .then(res => setMyRequests(res.data || []))
      .catch(() => message.error('Talepler yüklenemedi.'))
      .finally(() => setMyRequestsLoading(false));
  };

  const handleCancelRequest = (id) => {
    cancelParentRequest(id)
      .then(() => { message.success('Talep geri çekildi.'); loadMyRequests(); })
      .catch((e) => message.error(apiErrorMessage(e, 'Geri çekilemedi.')));
  };

  // "Gelmeyecegim" talebi - yaklasan (yoklamasi henuz girilmemis) dersleri yukler
  const openAbsenceRequest = () => {
    if (!selectedId) { message.warning('Önce öğrenci seçin.'); return; }
    setRequestModalType('absence');
    setSelectedSessionId(null);
    setRequestNote('');
    setUpcomingLoading(true);
    getUpcomingSessions(selectedId)
      .then(res => setUpcomingSessions(res.data.data || []))
      .catch(() => message.error('Yaklaşan dersler yüklenemedi.'))
      .finally(() => setUpcomingLoading(false));
  };

  // Telafi talebi - secilen bekleyen telafi hakki + onun icin uygun (bos)
  // gun/saat secenekleri (kendi haftalik dersi disinda, yarindan itibaren
  // yaklasik 1 hafta). "Uygun/Dolu" bilgisi disinda ogrenci sayisi gosterilmiyor.
  const openMakeupRequest = (makeupRightId) => {
    if (!selectedId) { message.warning('Önce öğrenci seçin.'); return; }
    setRequestModalType('makeup');
    setSelectedMakeupRightId(makeupRightId);
    setSelectedSessionId(null);
    setRequestNote('');
    setUpcomingLoading(true);
    getMakeupSlotOptions(selectedId)
      .then(res => setUpcomingSessions(res.data.data || []))
      .catch(() => message.error('Uygun gün/saat seçenekleri yüklenemedi.'))
      .finally(() => setUpcomingLoading(false));
  };

  const closeRequestModal = () => {
    setRequestModalType(null);
    setSelectedSessionId(null);
    setSelectedMakeupRightId(null);
    setRequestNote('');
  };

  const handleSubmitRequest = () => {
    if (!selectedSessionId) {
      message.warning('Lütfen bir gün/saat seçin.');
      return;
    }
    setSubmittingRequest(true);
    const payload = requestModalType === 'absence'
      ? { student_id: selectedId, type: 'absence', lesson_session_id: selectedSessionId, note: requestNote.trim() || undefined }
      : { student_id: selectedId, type: 'makeup', makeup_right_id: selectedMakeupRightId, lesson_session_id: selectedSessionId, note: requestNote.trim() || undefined };

    createParentRequest(payload)
      .then(() => {
        message.success('Talebiniz oluşturuldu, onay bekleniyor.');
        closeRequestModal();
        if (activeTab === 'requests') loadMyRequests();
      })
      .catch(e => message.error(apiErrorMessage(e, 'Talep oluşturulamadı.')))
      .finally(() => setSubmittingRequest(false));
  };

  // URL'den ?tab= gelirse o sekme acik baslasin (medya bildirimine tiklayinca
  // dogrudan Medya sekmesi acilsin diye). Gecerli sekme anahtari degilse
  // varsayilana dusuluyor. Ilgili listenin yuklenmesini asagidaki
  // [selectedId] effect'i ustleniyor (ogrenci geldiginde tetikleniyor).
  const TAB_KEYS = ['progress', 'attendance', 'makeup', 'media', 'requests'];
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(TAB_KEYS.includes(urlTab) ? urlTab : 'progress');

  const handleTabChange = key => {
    setActiveTab(key);
    if (key === 'attendance') { setAttPage(1); loadAttendances(1, selectedId); }
    if (key === 'makeup') { setMkPage(1); loadMakeups(1, selectedId); }
    if (key === 'requests') loadMyRequests();
    if (key === 'media') loadMedia(selectedId);
    if (key === 'progress') loadCertificates(selectedId);
  };

  // Öğrenci değişince aktif tab'ın listesini yenile
  useEffect(() => {
    if (activeTab === 'attendance') { setAttPage(1); loadAttendances(1, selectedId); }
    if (activeTab === 'makeup') { setMkPage(1); loadMakeups(1, selectedId); }
    if (activeTab === 'media') loadMedia(selectedId);
    if (activeTab === 'progress') loadCertificates(selectedId);
  }, [selectedId]);

  // "Sertifika Programları" sekmesi bu öğrencide hiç çizilmiyorsa (ilerleyiş
  // kapalı ve henüz tamamlanmış programı yok) varsayılan sekme boş ekran
  // bırakırdı — devam durumuna geçiriyoruz.
  useEffect(() => {
    const cocuk = children.find(c => c.id === selectedId);
    if (!cocuk) return;
    const gizli = cocuk.show_certificate_progress === false
      && (cocuk.certificates?.length ?? 0) === 0;
    if (gizli && activeTab === 'progress') handleTabChange('attendance');
  }, [selectedId, children]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dashLoading) return <div className="flex justify-center items-center h-64"><Spin size="large" /></div>;

  const selectedChild = children.find(c => c.id === selectedId);

  // Filter attendances client-side for multi-child
  const childAttendances = attendances.filter(a => !selectedId || a.student?.id === selectedId || a.student_id === selectedId);

  const attColumns = [
    {
      title: 'Tarih',
      key: 'date',
      render: (_, r) => r.lesson_session?.date
        ? dayjs(r.lesson_session.date).format('DD.MM.YYYY')
        : '—',
    },
    {
      title: 'Saat',
      key: 'time',
      render: (_, r) => r.lesson_session?.start_time?.slice(0, 5) || '—',
    },
    {
      title: 'Derslik',
      key: 'classroom',
      // İKİ HATA BİRDEN VARDI, ikisi de bu sütunu HER SATIRDA boş bırakıyordu:
      //
      // 1) `slotEnrollment` yazılmıştı; Laravel ilişkileri snake_case
      //    serileştiriyor, doğrusu `slot_enrollment`. Yanlış anahtar sessizce
      //    `undefined` dönüyordu.
      // 2) `SlotEnrollment` global `notDeleted` scope'u taşıyor — İPTAL
      //    EDİLMİŞ bir kayda ait satırda ilişki NULL geliyor, yani doğru
      //    anahtarla bile boş kalırdı.
      //
      // Bu yüzden önce SEANSIN KENDİ dersliği okunuyor (`lesson_sessions.
      // classroom_id` her zaman dolu ve iptalden etkilenmiyor); slot'unki
      // yalnızca yedek. (Veli ekranında ölçülüp bildirildi, 12.10.2026.)
      render: (_, r) => r.lesson_session?.classroom?.name
        || r.lesson_session?.slot_enrollment?.classroom?.name
        || '—',
    },
    ...(children.length > 1 ? [{
      title: 'Öğrenci',
      key: 'student',
      render: (_, r) => r.student ? `${r.student.name} ${r.student.surname}` : '—',
    }] : []),
    {
      title: 'Durum',
      dataIndex: 'status',
      render: s => (
        <Tag icon={STATUS_ICONS[s]} color={STATUS_COLORS[s]}>
          {STATUS_LABELS[s] || s}
        </Tag>
      ),
    },
  ];

  // Backend enum (MakeupRightController): pending/scheduled/completed/cancelled.
  const MAKEUP_STATUS = {
    pending:   { label: 'Planlanmadı', color: 'orange' },
    scheduled: { label: 'Planlandı',   color: 'blue' },
    completed: { label: 'Tamamlandı',  color: 'green' },
    cancelled: { label: 'İptal',       color: 'red' },
  };

  const mkColumns = [
    {
      // `date`/`start_time`/`classroom` KAÇIRILAN orijinal dersin bilgisidir;
      // gerçek telafi tarihi `scheduled_session` içindedir (henüz
      // planlanmadıysa null). Eskiden burada hep kaçırılan dersin tarihi
      // gösterilip, o geçmiş bir tarih olduğu için durum da hep "Tamamlandı"
      // hesaplanıyordu — telafi henüz planlanmamış olsa bile.
      title: 'Tarih',
      key: 'date',
      render: (_, r) => r.scheduled_session?.date
        ? dayjs(r.scheduled_session.date).format('DD.MM.YYYY')
        : <span className="text-gray-400 italic">Planlanmadı</span>,
    },
    {
      title: 'Saat',
      key: 'time',
      render: (_, r) => r.scheduled_session?.start_time
        ? `${r.scheduled_session.start_time.slice(0, 5)}${r.scheduled_session.end_time ? ` – ${r.scheduled_session.end_time.slice(0, 5)}` : ''}`
        : '—',
    },
    {
      title: 'Derslik',
      key: 'classroom',
      render: (_, r) => r.scheduled_session?.classroom || '—',
    },
    {
      title: 'Kaçırılan Ders',
      key: 'missed',
      render: (_, r) => r.date
        ? `${dayjs(r.date).format('DD.MM.YYYY')}${r.start_time ? ` ${r.start_time.slice(0, 5)}` : ''}${r.classroom ? ` · ${r.classroom}` : ''}`
        : '—',
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_, r) => {
        const st = MAKEUP_STATUS[r.status] || { label: r.status || '—', color: 'default' };
        return <Tag color={st.color}>{st.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'action',
      // Ayrılmış öğrencide talep düğmesi çıkmıyor — hak listesi görünmeye
      // devam ediyor (geçmiş bilgi), ama yeni talep açılamıyor. Sunucu da
      // reddediyor (ParentRequestController::store), buradaki gizleme
      // yalnızca kullanıcıyı boşuna uğraştırmamak için.
      render: (_, r) => r.status === 'pending' && !selectedChild?.is_passive && (
        <Button size="small" icon={<SendOutlined />} onClick={() => openMakeupRequest(r.id)}>
          Telafi Talep Et
        </Button>
      ),
    },
  ];

  const REQUEST_STATUS_TAGS = {
    pending: <Tag color="orange">Bekliyor</Tag>,
    approved: <Tag color="green">Onaylandı</Tag>,
    rejected: <Tag color="red">Reddedildi</Tag>,
    cancelled: <Tag color="default">Geri Çekildi</Tag>,
  };

  const requestColumns = [
    {
      title: 'Tür',
      key: 'type',
      render: (_, r) => r.type === 'absence' ? 'Devamsızlık Bildirimi' : 'Telafi Hakkı Kullanma',
    },
    {
      title: 'Detay',
      key: 'detail',
      render: (_, r) => {
        if (r.type === 'absence') {
          const ls = r.lesson_session;
          return ls ? `${dayjs(ls.date).format('DD.MM.YYYY')} ${ls.start_time?.slice(0, 5) || ''}` : '—';
        }
        const missed = r.makeup_right?.attendance?.lesson_session;
        const requested = r.lesson_session;
        const missedText = missed ? `Kaçırılan: ${dayjs(missed.date).format('DD.MM.YYYY')} ${missed.start_time?.slice(0, 5) || ''}` : '';
        const requestedText = requested ? `Talep edilen: ${dayjs(requested.date).format('DD.MM.YYYY')} ${requested.start_time?.slice(0, 5) || ''}` : '';
        return [missedText, requestedText].filter(Boolean).join(' — ') || '—';
      },
    },
    { title: 'Not', dataIndex: 'note', render: v => v || <span className="text-gray-400">—</span> },
    { title: 'Tarih', dataIndex: 'created_at', render: v => dayjs(v).format('DD.MM.YYYY HH:mm') },
    { title: 'Durum', dataIndex: 'status', render: v => REQUEST_STATUS_TAGS[v] || v },
    {
      title: '',
      key: 'action',
      render: (_, r) => r.status === 'pending' && (
        <Popconfirm
          title="Talebi geri çekmek istediğinize emin misiniz?"
          onConfirm={() => handleCancelRequest(r.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button size="small" danger>Geri Çek</Button>
        </Popconfirm>
      ),
    },
  ];

  // İlerleyişi kapatılmış ve henüz tamamlanmış programı olmayan öğrencide
  // "Sertifika Programları" sekmesi HİÇ ÇIKMIYOR. Boş bir sekme bırakmak
  // "kaydı yok" izlenimi verirdi; oysa çocuğun programı var, gösterilmiyor.
  // YARIŞMA ÇALIŞMASI — sertifika ilerleyişinden ayrı, DETAYSIZ.
  //
  // Yarışmaya hazırlanan öğrenci ders saatinde müfredat yerine yarışma
  // konularına çalışıyor. Eskiden bu ders veli ekranında bomboş görünüyordu
  // (öğretmen sertifika programı seçemediği için hiçbir şey yazamıyordu).
  //
  // Ne yapıldığı BİLEREK gösterilmiyor — yalnızca kaç derste çalışıldığı.
  // Kullanıcının kararı (04.10.2026): "detay vermesin, sadece yarışma için
  // çalışıldığı bilgisi yeterli". Başlığı yalnız bırakmayıp sayıyı da
  // vermemizin sebebi: içi boş bir başlık ekranda bozuk duruyor ve asıl
  // şikâyet zaten "veliler boş görüyor"du.
  const yarismaSayisi = selectedChild?.yarisma_ders_sayisi ?? 0;

  const yarismaBlogu = yarismaSayisi > 0 ? (
    <Card size="small" className="mb-4" style={{ borderColor: '#d3adf7' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <TrophyOutlined style={{ color: '#722ed1' }} />
        <span className="font-semibold">Yarışma Çalışmaları</span>
      </div>
      <div className="text-sm text-gray-500 mt-1">
        {yarismaSayisi} derste yarışma çalışması yapıldı.
      </div>
    </Card>
  ) : null;

  // Yarışma çalışması varsa sekme GİZLENMEZ — gösterilecek bir şey var.
  const sertifikaSekmesiGizli =
    selectedChild?.show_certificate_progress === false &&
    (selectedChild?.certificates?.length ?? 0) === 0 &&
    yarismaSayisi === 0;

  const tabs = [
    {
      key: 'progress',
      label: <span><BookOutlined className="mr-1" />Sertifika Programları</span>,
      children: (
        <div>
          {yarismaBlogu}
          {!selectedChild ? (
            <Empty description="Öğrenci seçiniz" />
          ) : selectedChild.certificates?.length === 0 ? (
            // Yarışma bloğu çizildiyse ayrıca "kaydı yok" yazmıyoruz —
            // ekranda gösterilecek bir şey zaten var.
            yarismaSayisi > 0 ? null : <Empty description="Sertifika programı kaydı yok" />
          ) : (() => {
            // Backend certificates'i course_id'ye gore (subenin sertifika
            // programlarini ekleme sirasi - genelde Temel -> Ileri gibi
            // pedagojik sirayi yansitiyor) sirali donuyor, burada sadece
            // devam eden/tamamlanan olarak ikiye ayiriyoruz (#82).
            const inProgress = selectedChild.certificates.filter(c => c.progress < 100);
            const completed  = selectedChild.certificates.filter(c => c.progress === 100);
            const studentCerts = certsByStudent[selectedId] || [];

            const renderProgressBar = (c) => (
              <div key={c.course_id} className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold">{c.certificate_name}</span>
                  <span className="text-xs text-gray-400">{c.completed_topics}/{c.total_topics} konu</span>
                </div>
                <Progress
                  percent={c.progress}
                  size="small"
                  strokeColor="#1677ff"
                  format={p => `%${p}`}
                />
              </div>
            );

            // Tamamlanan program icin gercek Certificate kaydi varsa ve PDF'i
            // hazirsa goruntule/indir butonlari, aksi halde neden henuz
            // gorunmedigini aciklayan bir bilgi metni gosteriliyor.
            const renderCompleted = (c) => {
              const cert = studentCerts.find(x => x.course_id === c.course_id);
              return (
                <div key={c.course_id} className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold">{c.certificate_name}</span>
                    <span className="text-xs text-gray-400">{c.completed_topics}/{c.total_topics} konu</span>
                  </div>
                  <Progress percent={100} size="small" strokeColor="#52c41a" format={p => `%${p}`} />
                  <div className="mt-2">
                    {certLoading ? (
                      <Spin size="small" />
                    ) : cert?.pdf_path ? (
                      <div className="flex gap-2">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(cert.view_url, '_blank', 'noopener,noreferrer')}>
                          Görüntüle
                        </Button>
                        <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownloadCertificate(cert)}>
                          İndir
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        Sertifikanız hazırlanıyor, kısa süre içinde burada görüntülenebilecek.
                      </span>
                    )}
                  </div>
                </div>
              );
            };

            // İlerleyiş bu öğrenci için kapatılmışsa "Devam Eden" bölümü hiç
            // çizilmiyor. Sunucu zaten devam eden satırları göndermiyor; burada
            // sekmeyi de kaldırmazsak veli "Devam eden sertifika programı yok"
            // cümlesini görür ve bu YANLIŞ olur — çocuğun programı var, sadece
            // gösterilmiyor. Tamamlananlar (ve indirme butonları) duruyor.
            if (selectedChild.show_certificate_progress === false) {
              return completed.map(renderCompleted);
            }

            return (
              <ResponsiveTabs
                size="small"
                items={[
                  {
                    key: 'in_progress',
                    label: `Devam Eden (${inProgress.length})`,
                    children: inProgress.length === 0
                      ? <Empty description="Devam eden sertifika programı yok" />
                      : inProgress.map(renderProgressBar),
                  },
                  {
                    key: 'completed',
                    label: `Tamamlanan (${completed.length})`,
                    children: completed.length === 0
                      ? <Empty description="Tamamlanan sertifika programı yok" />
                      : completed.map(renderCompleted),
                  },
                ]}
              />
            );
          })()}
        </div>
      ),
    },
    {
      key: 'attendance',
      label: <span><CheckCircleOutlined className="mr-1" />Devam Durumu</span>,
      children: (
        <div>
          <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
            {attSummary ? (
              <div className="flex gap-4 text-sm">
                {selectedId && children.length > 1 && (
                  <span className="font-medium text-gray-700">
                    {children.find(c => c.id === selectedId)?.name}:
                  </span>
                )}
                <span className="text-green-600 font-medium">Geldi: {attSummary.present || 0}</span>
                <span className="text-red-500 font-medium">Gelmedi: {attSummary.absent || 0}</span>
                {(attSummary.planned_absent || 0) > 0 && (
                  <span className="text-orange-500 font-medium">Planlı: {attSummary.planned_absent}</span>
                )}
              </div>
            ) : <div />}
            {!selectedChild?.is_passive && (
              <Button size="small" icon={<SendOutlined />} onClick={openAbsenceRequest}>
                Gelmeyeceğim Bildir
              </Button>
            )}
          </div>
          <ResponsiveTable
            size="small"
            scroll={{ x: 'max-content' }}
            loading={attLoading}
            dataSource={attendances}
            columns={attColumns}
            rowKey="id"
            locale={{ emptyText: <Empty description="Devam kaydı yok" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            pagination={{
              current: attPage,
              pageSize: 10,
              total: attTotal,
              onChange: p => { setAttPage(p); loadAttendances(p, selectedId); },
              showTotal: t => `Toplam ${t} kayıt`,
            }}
          />
        </div>
      ),
    },
    {
      key: 'makeup',
      label: <span><ToolOutlined className="mr-1" />Telafi Dersleri</span>,
      children: (
        <ResponsiveTable
          size="small"
          scroll={{ x: 'max-content' }}
          loading={mkLoading}
          dataSource={makeups}
          columns={mkColumns}
          rowKey="id"
          locale={{ emptyText: <Empty description="Telafi dersi yok" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={{
            current: mkPage,
            pageSize: 10,
            total: mkTotal,
            onChange: p => { setMkPage(p); loadMakeups(p); },
            showTotal: t => `Toplam ${t} kayıt`,
          }}
        />
      ),
    },
    {
      key: 'media',
      label: <span><PictureOutlined className="mr-1" />Medya</span>,
      children: (
        <ResponsiveTable
          size="small"
          scroll={{ x: 'max-content' }}
          loading={mediaLoading}
          dataSource={media}
          rowKey="id"
          locale={{ emptyText: <Empty description="Henüz medya yüklenmemiş" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={false}
          columns={[
            {
              title: 'Dosya',
              dataIndex: 'original_name',
              render: (name, r) => (
                <span className="flex items-center gap-2">
                  {r.type === 'photo' ? <FileImageOutlined className="text-purple-500" /> : <VideoCameraOutlined className="text-blue-500" />}
                  <span className="text-sm">{name || `${r.type === 'photo' ? 'Fotoğraf' : 'Video'} ${r.id}`}</span>
                </span>
              ),
            },
            { title: 'Tarih', dataIndex: 'uploaded_at', width: 130, render: v => <span className="text-sm">{v}</span> },
            { title: 'Yükleyen', dataIndex: 'uploader', width: 160, render: v => <span className="text-sm text-gray-600">{v || '-'}</span> },
            {
              title: '',
              key: 'actions',
              width: 200,
              render: (_, r) => (
                <div className="flex gap-1">
                  <Button size="small" icon={<PlayCircleOutlined />} onClick={() => setPreview({ url: r.stream_url, type: r.type })}>
                    {r.type === 'photo' ? 'Görüntüle' : 'Oynat'}
                  </Button>
                  <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownloadMedia(r)}>
                    İndir
                  </Button>
                </div>
              ),
            },
          ]}
        />
      ),
    },
    {
      key: 'requests',
      label: <span><FileSearchOutlined className="mr-1" />Taleplerim</span>,
      children: (
        <ResponsiveTable
          size="small"
          scroll={{ x: 'max-content' }}
          loading={myRequestsLoading}
          dataSource={myRequests}
          columns={requestColumns}
          rowKey="id"
          locale={{ emptyText: <Empty description="Henüz talep oluşturmadınız" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={false}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold mb-0">Öğrencilerim</h2>
        <ChildSelector children={children} value={selectedId} onChange={setSelectedId} />
      </div>

      {/*
        Seçicideki rozet TEK ÇOCUKLU velide görünmüyor (seçici hiç
        çizilmiyor), o yüzden durum burada ayrıca yazılıyor. Bu kutu aynı
        zamanda "düğmeler nereye gitti" sorusunun cevabı — düğmeleri
        açıklamasız kaldırmak, ayrı bir şikâyet doğururdu.
      */}
      {selectedChild?.is_passive && (
        <Alert
          type="warning"
          showIcon
          message={`${selectedChild.name} için kayıt şu anda devam etmiyor.`}
          description="Geçmiş kayıtlar, sertifikalar ve ödeme bilgileri görüntülenmeye devam eder; yeni devamsızlık veya telafi talebi oluşturulamaz. Kaydı yeniden başlatmak için bizimle iletişime geçebilirsiniz."
        />
      )}

      {children.length === 0 ? (
        <Card><Empty description="Kayıtlı öğrenci yok." /></Card>
      ) : (
        <Card size="small">
          <ResponsiveTabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={sertifikaSekmesiGizli ? tabs.filter(t => t.key !== 'progress') : tabs}
          />
        </Card>
      )}

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width={800}
        destroyOnClose
        title={preview?.type === 'photo' ? 'Fotoğraf' : 'Video'}
      >
        {preview?.type === 'photo' ? (
          <img src={preview.url} alt="" style={{ width: '100%', borderRadius: 8 }} />
        ) : preview && (
          <video src={preview.url} controls autoPlay style={{ width: '100%', borderRadius: 8 }} />
        )}
      </Modal>

      <Modal
        title={requestModalType === 'absence' ? 'Gelmeyeceğim Bildir' : 'Telafi Hakkı Kullanma Talebi'}
        open={!!requestModalType}
        onCancel={closeRequestModal}
        onOk={handleSubmitRequest}
        confirmLoading={submittingRequest}
        okText="Talep Gönder"
        cancelText="Vazgeç"
        destroyOnClose
      >
        {requestModalType === 'absence' ? (
          <>
            <p className="text-gray-500 mb-2">
              Öğretmen/yönetici onayladıktan sonra bu ders "Gelmeyecek" olarak kaydedilecek.
            </p>
            <Select
              value={selectedSessionId}
              onChange={setSelectedSessionId}
              loading={upcomingLoading}
              style={{ width: '100%' }}
              placeholder="Ders seçin"
              notFoundContent={upcomingLoading ? 'Yükleniyor...' : 'Yaklaşan uygun ders bulunamadı'}
              options={upcomingSessions.map(s => ({
                value: s.id,
                label: `${dayjs(s.date).format('DD.MM.YYYY')} ${s.start_time?.slice(0, 5) || ''} · ${s.slot_enrollment?.classroom?.name || ''}`,
              }))}
            />
          </>
        ) : (
          <>
            <p className="text-gray-500 mb-2">
              Önümüzdeki bir hafta içinden uygun bir gün/saat seçin. Onaylandığında telafiniz otomatik olarak bu güne planlanacaktır.
            </p>
            <Select
              value={selectedSessionId}
              onChange={setSelectedSessionId}
              loading={upcomingLoading}
              style={{ width: '100%' }}
              placeholder="Gün/saat seçin"
              notFoundContent={upcomingLoading ? 'Yükleniyor...' : 'Uygun gün/saat bulunamadı'}
              options={upcomingSessions.map(s => ({
                value: s.id,
                disabled: !s.available,
                label: (
                  <span>
                    {dayjs(s.date).format('DD.MM.YYYY, dddd')} {s.start_time?.slice(0, 5) || ''} · {s.classroom}
                    {' '}
                    <Tag color={s.available ? 'green' : 'red'} style={{ marginLeft: 4 }}>
                      {s.available ? 'Uygun' : 'Dolu'}
                    </Tag>
                  </span>
                ),
              }))}
            />
          </>
        )}
        <TextArea
          className="mt-3"
          value={requestNote}
          onChange={(e) => setRequestNote(e.target.value)}
          placeholder="Not (opsiyonel)..."
          autoSize={{ minRows: 2, maxRows: 5 }}
          maxLength={1000}
        />
      </Modal>
    </div>
  );
};

export default ParentStudents;
